import { CustomFieldProps, ReportDTO } from '@nx-smartmonkey/shared/domain';
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { STOP_STATUS } from '../../constants/Stops';
import { DriverProjectFields } from '../../domain/driversProject/DriversProject';
import { Route } from '../../domain/route/Route';
import { Stop } from '../../domain/stops/Stop';
import { StopReport } from '../../domain/stops/StopReport';
import { RouteMapper } from '../../mappers/RouteMapper';
import StopMapper from '../../mappers/StopMapper';
import { StopReportMapper } from '../../mappers/StopReportMapper';
import { StopTaskMapper } from '../../mappers/StopTaskMapper';
import { SessionRepository } from '../session/SessionRepository';
import {
  CreateReportCancelProps,
  CreateReportCompleteProps,
  CreateReportPropsApi,
  StopRepository,
} from './StopRepository';

function convertFields(custom_fields: CustomFieldProps, serviceReportProjectField: DriverProjectFields) {
  const customFields = custom_fields;

  for (const key in customFields) {
    const fieldInfo = serviceReportProjectField.find((field) => field.id === key);

    if (fieldInfo) {
      if (fieldInfo.type === `number`) {
        customFields[key] = Number(customFields[key]);
      }
      if (fieldInfo.type === `select` || fieldInfo.type === `multiselect`) {
        const content = customFields[key] as number[];

        content.forEach((value) => {
          if (value === -1) {
            content.splice(content.indexOf(value), 1);
          }
        });

        if (content.length === 0) {
          delete customFields[key];
        }
      }
    }
  }
  return customFields;
}

export class StopRepositoryApi implements StopRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async createReport({
    currentPosition,
    status,
    stopId,
    reportData,
    images,
    timeInStop,
    project,
  }: CreateReportPropsApi<CreateReportCompleteProps | CreateReportCancelProps>): Promise<StopReport> {
    let params: Record<string, any> = {};

    if (reportData) {
      if (status === STOP_STATUS.canceled) {
        params = {
          comments: (reportData as CreateReportCancelProps).comments,
          custom_fields: (reportData as CreateReportCancelProps).customFields
            ? convertFields(
                (reportData as CreateReportCancelProps).customFields!.props,
                project.fields.stop_canceled_fields
              )
            : undefined,
          cancel_reason: (reportData as CreateReportCancelProps).cancelReason,
        };
      } else {
        params = {
          pin: (reportData as CreateReportCompleteProps).pin,
          comments: (reportData as CreateReportCompleteProps).comments,
          signature: (reportData as CreateReportCompleteProps).signature,
          custom_fields: (reportData as CreateReportCancelProps).customFields
            ? convertFields(
                (reportData as CreateReportCancelProps).customFields!.props,
                project.fields.stop_completed_fields
              )
            : undefined,
          reportTasks: (reportData as CreateReportCompleteProps).reportTasks?.map((reportTask) => {
            const { task_id, status, comments, custom_fields } = reportTask.value;
            return {
              task_id,
              status,
              comments,
              custom_fields: custom_fields
                ? convertFields(custom_fields!.props, project.fields.task_fields)
                : undefined,
            };
          }),
          temporalReportTasks: (reportData as CreateReportCompleteProps).temporalReportTasks?.map((reportTask) => {
            const { task_id, status, comments, custom_fields } = reportTask.value;

            return {
              task_id,
              status,
              comments,
              custom_fields:
                status !== `pending` && custom_fields
                  ? convertFields(
                      custom_fields!.props,
                      status === `completed`
                        ? project.fields.task_completed_fields
                        : project.fields.task_canceled_fields
                    )
                  : undefined,
            };
          }),
          temporalTasks: (reportData as CreateReportCompleteProps).temporalTasks?.map(StopTaskMapper.toRepository),
        };
      }
    }

    if (currentPosition) {
      const { lat, lng } = currentPosition;
      params.done_location = { lat, lng };
    }

    if (timeInStop) {
      params.time_in_stop = timeInStop;
    }

    const formData = new FormData();
    formData.append(`report_params`, JSON.stringify(params));
    if ((images ?? []).length > 0) {
      const filesMetadata: { name: string; mime: string }[] = [];
      (images ?? []).forEach((image, index) => {
        const metadata = {
          name: image.file_name,
          mime: image.mime_type,
        };

        filesMetadata.push(metadata);

        formData.append(`file_${index}`, image.data);
      });
      formData.append(`files_metadata`, JSON.stringify(filesMetadata));
    }

    const urlParams = new URLSearchParams();

    urlParams.append(`status`, status === `canceled` ? `service_report_canceled` : `service_report_completed`);

    const response = await this.requests.post({
      uri: `/drivers/v3/stop/${stopId}/report?${urlParams.toString()}`,
      data: formData,
      headers: {
        'content-type': `multipart/form-data`,
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    const createdReport = StopReportMapper.toDomain(response.data as ReportDTO);
    return createdReport;
  }

  async discardReport({ stop }: { stop: Stop }): Promise<Stop> {
    if (!stop.report) {
      throw new Error(`Stop report not found`);
    }

    await this.requests.delete({
      uri: `/drivers/v3/stop/${stop.id}/report/${stop.report.id}`,
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    stop.status = `pending`;
    stop.report = undefined;
    return stop;
  }

  async validatePin({ stop, pin }: { stop: Stop; pin: string }): Promise<void> {
    await this.requests.post({
      uri: `/drivers/v3/stop/${stop.id}/pin`,
      data: {
        pin,
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async sendPin({ stop, method }: { stop: Stop; method: `email` | `sms` }): Promise<void> {
    await this.requests.post({
      uri: `/drivers/v3/stop/${stop.id}/pin/send`,
      data: {
        method,
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async updateStop({ newStop, stopId }: { newStop: Stop; stopId: string }): Promise<Stop> {
    const newStopDTO = StopMapper.toDTO(newStop);

    const response = await this.requests.put({
      uri: `/drivers/v3/stop/${stopId}`,
      data: {
        newStop: newStopDTO,
      },
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return StopMapper.toDomain(response.data);
  }

  async deleteStop({ stop }: { stop: Stop }): Promise<Route> {
    const response = await this.requests.delete({
      uri: `/drivers/v3/stop/${stop.id}`,
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return RouteMapper.toDomain(response.data);
  }

  async deleteStops({ stops }: { stops: string[] }): Promise<Route> {
    const response = await this.requests.put({
      uri: `/drivers/v3/stops`,
      data: {
        stops_ids: stops,
      },
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return RouteMapper.toDomain(response.data);
  }
  async copyStops({
    route_destination_id,
    route_origin_id,
    stops,
  }: {
    route_destination_id: string;
    route_origin_id: string;
    stops: string[];
  }): Promise<Route> {
    const response = await this.requests.post({
      uri: `/drivers/v3/stops/copy/${route_destination_id}`,
      data: {
        route_origin_id,
        stop_ids: stops,
      },
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return RouteMapper.toDomain(response.data);
  }
}
